import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CalendarClock } from 'lucide-react'
import { Panel } from '@/components/erp/primitives'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { Field } from '@/shared/ui/Modal'
import { explain } from '@/shared/api/problem'
import { fmtDateTime } from '@/shared/lib/format'
import type { SurveyResponseDto, SurveyScheduleDto } from '@/shared/api/types'
import { getSchedule, openNextWave, setSchedule, updatePoll } from './api'
import { useCodes } from './useVocabulary'
import { ChoiceSelect, StepField } from './ConfigChoice'
import { frequencyImplication, frequencyLabel } from './vocab'
import { DateTimePicker } from '@/components/erp/date-picker'
import { cn } from '@/lib/utils'

/**
 * Step 4 — when this poll runs, and when its next wave opens.
 *
 * ## Why this screen can be trusted and PollSphere's could not
 *
 * Its configurator offered a frequency, a cron pattern and a next-run
 * timestamp. Nothing in its source read any of the three, so a recurring
 * tracker there was a label on a one-off poll and the only way to find out was
 * to wait for a wave that never came.
 *
 * Three things make this one different, and all three are visible on the panel:
 *
 * - **There is no cron box.** The frequency IS the recurrence rule on the
 *   server. A free-text second source of truth for "how often" is the shape of
 *   the original bug.
 * - **The projected dates are printed.** A schedule nobody can see is a
 *   schedule nobody can check, so the server returns the next six run instants
 *   and this renders them.
 * - **The refusal arrives before the toggle.** `autoWaveBlocked` says why
 *   automatic waves cannot be switched on — no tracker, or a frequency with no
 *   interval — so the reason is on screen rather than behind a failed save.
 */
/**
 * The three positions, with the consequence of each on the card rather than in
 * a tooltip. "SOFT" and "HARD" mean nothing to somebody configuring a poll;
 * "accepted and flagged" and "refused at submission" mean everything.
 */
const FENCE_MODES = [
  {
    code: 'OFF', label: 'Off',
    implication: 'Coordinates are still recorded on every interview. Nothing is refused and '
      + 'nothing is flagged for being out of area.',
  },
  {
    code: 'SOFT', label: 'Flag it',
    implication: 'An out-of-area interview is KEPT and raised for the office. The right default '
      + 'for most polls — a refused interview cannot be offered again.',
  },
  {
    code: 'HARD', label: 'Refuse it',
    implication: 'Refused at submission, with the distance. For the poll where a wrong-place '
      + 'interview is worse than a lost one — an exit poll at a named station.',
  },
] as const

export function ConfigScheduleStep({ poll, canUpdate, canCreate, onSaved }: {
  poll: SurveyResponseDto
  canUpdate: boolean
  canCreate: boolean
  onSaved: (poll: SurveyResponseDto) => void
}) {
  const frequencies = useCodes('frequency')
  const [schedule, setScheduleState] = useState<SurveyScheduleDto | null>(null)
  const [frequency, setFrequency] = useState(poll.frequencyCode)
  const [autoWave, setAutoWave] = useState(false)
  const [firstRunAt, setFirstRunAt] = useState('')
  const [opensAt, setOpensAt] = useState(toLocalInput(poll.opensAt))
  const [closesAt, setClosesAt] = useState(toLocalInput(poll.closesAt))
  const [fenceMode, setFenceMode] = useState(poll.fenceModeCode ?? 'OFF')
  // A string, not a number: an empty box means "use the deployment default",
  // and a number state would have to spell that as 0 or NaN.
  const [fenceRadius, setFenceRadius] = useState(
    poll.fenceRadiusM ? String(poll.fenceRadiusM) : '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    try {
      const next = await getSchedule(poll.id)
      setScheduleState(next)
      setFrequency(next.frequencyCode)
      setAutoWave(next.autoWave)
      setFirstRunAt(toLocalInput(next.nextRunAt))
    } catch (e) {
      setError(explain(e))
    }
  }, [poll.id])

  useEffect(() => { void reload() }, [reload])
  useEffect(() => {
    setOpensAt(toLocalInput(poll.opensAt))
    setClosesAt(toLocalInput(poll.closesAt))
  }, [poll.id, poll.opensAt, poll.closesAt])
  useEffect(() => {
    setFenceMode(poll.fenceModeCode ?? 'OFF')
    setFenceRadius(poll.fenceRadiusM ? String(poll.fenceRadiusM) : '')
  }, [poll.id, poll.fenceModeCode, poll.fenceRadiusM])

  /**
   * Every field the poll already has.
   *
   * The server's update REPLACES the poll, so a saver that omits a field it did
   * not edit clears it. Three panels on this screen save, and spelling the
   * carry-through list out three times is how one of them ends up missing the
   * field added last week — which is a silent data loss, not a type error.
   */
  function carried() {
    return {
      title: poll.title,
      description: poll.description,
      categoryCode: poll.categoryCode,
      purposeCode: poll.purposeCode,
      audienceCode: poll.audienceCode,
      frequencyCode: frequency,
      methodologyCode: poll.methodologyCode,
      scopeUnitId: poll.scopeUnitId,
      groupId: poll.groupId,
      waveId: poll.waveId,
      targetSample: poll.targetSample,
      confidenceLevel: poll.confidenceLevel,
      targetMoe: poll.targetMoe,
      linkedSeatType: poll.linkedSeatType,
      audioRecordingMode: poll.audioRecordingMode,
      consentRequired: poll.consentRequired,
      randomiseQuestionOrder: poll.randomiseQuestionOrder,
      randomiseOptionOrder: poll.randomiseOptionOrder,
      allowOffline: poll.allowOffline,
      fenceModeCode: poll.fenceModeCode,
      fenceRadiusM: poll.fenceRadiusM,
      opensAt: poll.opensAt,
      closesAt: poll.closesAt,
      retentionDays: poll.retentionDays,
    }
  }

  async function saveWindow() {
    setBusy(true)
    setError(null)
    try {
      onSaved(await updatePoll(poll.id, {
        ...carried(),
        opensAt: fromLocalInput(opensAt),
        closesAt: fromLocalInput(closesAt),
      }))
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  async function saveFence() {
    setBusy(true)
    setError(null)
    try {
      onSaved(await updatePoll(poll.id, {
        ...carried(),
        fenceModeCode: fenceMode,
        // Blank means "the deployment's default", which is what null says to
        // the server. Sending the resolved number instead would freeze today's
        // default onto the poll and make a later change to it invisible here.
        fenceRadiusM: fenceRadius.trim() ? Number(fenceRadius) : null,
      }))
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  async function saveRecurrence() {
    setBusy(true)
    setError(null)
    try {
      setScheduleState(await setSchedule(poll.id, {
        frequencyCode: frequency,
        autoWave,
        firstRunAt: fromLocalInput(firstRunAt),
      }))
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  async function openWave() {
    setBusy(true)
    setError(null)
    try {
      await openNextWave(poll.id)
      await reload()
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      <Panel title="Fieldwork window"
        description="When this poll accepts responses"
        right={canUpdate && (
          <button type="button" className="erp-btn-primary h-8 text-erp-xs"
            disabled={busy} onClick={saveWindow}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        )}
      >
        <div className="space-y-3">
          <ErrorStrip message={error} />
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Opens at" hint="Before this, a live poll still refuses responses.">
              <DateTimePicker value={opensAt} onChange={setOpensAt} className="h-9 w-full" />
            </Field>
            <Field label="Closes at" hint="After this, submissions are refused with a reason.">
              <DateTimePicker value={closesAt} onChange={setClosesAt} className="h-9 w-full" />
            </Field>
          </div>
          <Note>
            The window is ENFORCED at submit time, not decorative. A draft, closed or archived
            poll accepts nothing, and neither does a live one outside its own dates.
          </Note>
        </div>
      </Panel>

      <Panel title="Where it may be answered"
        description="The geo-fence, checked at submission"
        right={canUpdate && (
          <button type="button" className="erp-btn erp-btn-primary h-8 text-erp-xs"
            disabled={busy} onClick={saveFence}>
            {busy ? 'Saving…' : 'Save fence'}
          </button>
        )}
      >
        <div className="space-y-3">
          <StepField label="Fence"
            hint="Coordinates are recorded on every interview whichever of these is chosen. This decides what happens when they do not match.">
            <div className="grid gap-2 sm:grid-cols-3">
              {FENCE_MODES.map((mode) => (
                <label key={mode.code}
                  className={cn('border rounded-md p-3 cursor-pointer block',
                    fenceMode === mode.code
                      ? 'border-chocolate bg-chocolate/5'
                      : 'border-border hover:border-muted-foreground/40',
                    !canUpdate && 'cursor-not-allowed opacity-60')}
                >
                  <span className="flex items-center gap-2">
                    <input type="radio" className="h-4 w-4 accent-chocolate"
                      name="fence-mode" value={mode.code} disabled={!canUpdate}
                      checked={fenceMode === mode.code}
                      onChange={() => setFenceMode(mode.code)} />
                    <span className="text-erp-sm font-medium">{mode.label}</span>
                  </span>
                  <span className="text-erp-xs text-muted-foreground block mt-1">
                    {mode.implication}
                  </span>
                </label>
              ))}
            </div>
          </StepField>

          {fenceMode !== 'OFF' && (
            <>
              <Field label="Radius for a polling station (metres)"
                hint="Only used where the unit has no boundary. Leave blank to use the deployment's default.">
                <input
                  className="erp-input h-9 w-full sm:w-48"
                  type="number" min={25} max={20000} step={25}
                  value={fenceRadius} disabled={!canUpdate}
                  placeholder={`Default — ${poll.fenceRadiusM ?? 300} m`}
                  onChange={(e) => setFenceRadius(e.target.value)} />
              </Field>

              <Note tone="info">
                <span className="font-medium">A ward is fenced against its real boundary</span>
                {' and ignores the radius. A polling station is a point in this system — it has '}
                {'coordinates and no polygon — so its fence can only be a circle, and 150 m '}
                {'round a school gate is a different promise from 150 m round a rural trading '}
                {'centre. Each interviewer is fenced against the unit on '}
                <Link to="/polling/fieldwork" className="underline">their own posting</Link>
                {', not against this poll\u2019s scope: a county-wide poll worked station by '}
                {'station is as many fences as there are stations.'}
              </Note>

              <Note>
                <span className="font-medium">What a fence proves, and what it does not.</span>
                {' It catches the honest failure — the wrong ward, the right ward on the wrong '}
                {'day, a phone that never left the office. It cannot catch somebody determined '}
                {'to cheat: a mock-location app is free and this system cannot tell its reading '}
                {'from a real one. Treat an interview that passed the fence as one whose phone '}
                {'reported the right place, never as proof of where it happened.'}
              </Note>

              {fenceMode === 'HARD' && (
                <Note tone="warn">
                  <span className="font-medium">HARD refuses at submission, and the field app
                    cannot offer a refused interview again.</span>
                  {' A reading too vague to place anybody — accuracy worse than '}
                  {'survey.gps.max-accuracy-m — is refused too, because the easiest way through '}
                  {'a fence that waved those through would be to supply one. Under a tree with '}
                  {'a 400 m fix, a real interview is lost. SOFT keeps it and tells the office.'}
                </Note>
              )}
            </>
          )}
        </div>
      </Panel>

      <Panel title="Recurrence"
        description="How often this becomes a new wave"
        right={canUpdate && (
          <button type="button" className="erp-btn-primary h-8 text-erp-xs"
            disabled={busy} onClick={saveRecurrence}>
            {busy ? 'Saving…' : 'Save recurrence'}
          </button>
        )}
      >
        {schedule === null ? <LoadingBlock label="Reading the schedule…" /> : (
          <div className="space-y-4">
            <StepField label="Frequency">
              <ChoiceSelect state={frequencies} value={frequency} onChange={setFrequency}
                labelOf={frequencyLabel} implicationOf={frequencyImplication}
                disabled={!canUpdate} />
            </StepField>

            {schedule.autoWaveBlocked ? (
              <Note>
                {schedule.autoWaveBlocked}
                {!poll.groupId && (
                  <>
                    {' '}
                    <Link to="/polling/trackers" className="underline">Trackers</Link>
                    {' is where one is created; step 1 is where this poll joins it.'}
                  </>
                )}
              </Note>
            ) : (
              <>
                <label className="flex items-start gap-2 border border-border rounded-md p-3 cursor-pointer">
                  <input type="checkbox" className="mt-0.5 h-4 w-4 accent-chocolate"
                    checked={autoWave} disabled={!canUpdate}
                    onChange={(e) => setAutoWave(e.target.checked)} />
                  <span className="min-w-0">
                    <span className="text-erp-sm font-medium block">Open the next wave automatically</span>
                    <span className="text-erp-xs text-muted-foreground block mt-0.5">
                      On the due date the scheduler creates the next wave in
                      {schedule.groupName ? ` “${schedule.groupName}”` : ' this tracker'} and a
                      new poll inside it — as a DRAFT, carrying a copy of this poll's published
                      instrument, its sample design, its consent scripts and its links.
                      It arrives {schedule.spawnLeadDays} day
                      {schedule.spawnLeadDays === 1 ? '' : 's'} early so fieldwork can staff it.
                      It is never launched for you: staffing names an interviewer, and no rule
                      can pick one.
                    </span>
                  </span>
                </label>

                {autoWave && (
                  <Field label="First run"
                    hint="Must be ahead. A date in the past would spawn immediately and then keep spawning for every interval already missed.">
                    <DateTimePicker
                      value={firstRunAt}
                      onChange={setFirstRunAt}
                      disabled={!canUpdate}
                      className="h-9 w-full"
                    />
                  </Field>
                )}
              </>
            )}

            {schedule.projectedRuns.length > 0 && (
              <div className="border border-border rounded-md p-3">
                <p className="text-erp-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
                  <CalendarClock className="h-3.5 w-3.5" />
                  The next waves, on this cadence
                </p>
                <ul className="grid gap-1 sm:grid-cols-3">
                  {schedule.projectedRuns.map((run) => (
                    <li key={run} className="text-erp-xs font-mono">{fmtDateTime(run)}</li>
                  ))}
                </ul>
                <p className="text-erp-xs text-muted-foreground mt-1.5">
                  Printed rather than described. A schedule a person cannot see is a schedule
                  they cannot check, which is how a cron string nothing executed survived two
                  releases in the system this replaces.
                </p>
              </div>
            )}

            <div className="flex items-center justify-between gap-3 border-t border-border pt-3">
              <p className="text-erp-xs text-muted-foreground min-w-0">
                {schedule.lastSpawnedAt
                  ? `A wave was last opened from this poll on ${fmtDateTime(schedule.lastSpawnedAt)}.`
                  : 'No wave has been opened from this poll yet.'}
                {schedule.spawnedFromSurveyId && ' This poll was itself opened by the scheduler.'}
              </p>
              {canCreate && poll.groupId && (
                <button type="button" className="erp-btn-ghost h-8 text-erp-xs shrink-0"
                  disabled={busy} onClick={openWave}>
                  Open the next wave now
                </button>
              )}
            </div>
          </div>
        )}
      </Panel>
    </div>
  )
}

/** ISO instant → the `datetime-local` value the browser wants, in local time. */
function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** And back. The server stores instants; the input speaks local wall-clock. */
function fromLocalInput(value: string): string | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}
